import { useEffect, useRef, useState } from 'react';
import type { ActionId, IsoDate, ObjectiveId, ObjectiveSummaryView, ObjectiveView, PriorityView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { CheckControl } from '@ui/shared/primitives/CheckControl';
import { Plus, Trash2, ChevronDown } from '@ui/shared/icons';
import { objectiveCategoryTokenPrefix, PickerOptionRow } from '@ui/desktop/objective-form/FormPrimitives';
import { CollapsibleCard } from './CollapsibleCard';
import { ListSheet } from './ListSheet';

const PRIORITY_CAP = 5;
const PHONE_ROW_CAP = 3;

const INPUT_STYLE = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
} as const;

/**
 * Phone Priorities (R8.11, today-phone-parity.md §4.5). The domain cap stays
 * 5 (unchanged, R8.2) — only the ROW-LIST rendering caps lower, at 3 visible
 * + a "+N more" fold into a bottom sheet (`ListSheet`), mirroring the
 * existing `DUE_TODAY_ROW_CAP` fold pattern already proven on this screen.
 * No hover-reveal anywhere — expand/comment/edit icons are always visible
 * at reduced weight (there is no hover on a touch surface).
 */
export function PrioritiesCardPhone({
  date,
  priorities,
  objectives,
  onChanged,
  onOpenObjective,
  isLast,
}: {
  readonly date: IsoDate;
  readonly priorities: readonly PriorityView[];
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onChanged: () => void;
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly isLast: boolean;
}) {
  const api = useDomainApi();
  const [composerOpen, setComposerOpen] = useState(false);
  const [orphanedId, setOrphanedId] = useState<string | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  // Bug fix (caught live, not by component tests): this card is not
  // remounted when the viewed `date` changes (backfill/calendar paging
  // re-renders the same TodayView tree against a new date) -- without this,
  // the overflow sheet/composer/orphaned-state from one date stayed open
  // and visible after navigating to a different date that may have zero
  // (or entirely different) priorities, showing a stale or empty sheet.
  useEffect(() => {
    setComposerOpen(false);
    setOrphanedId(null);
    setOverflowOpen(false);
  }, [date]);

  const doneCount = priorities.filter((p) => p.completed).length;
  const capReached = priorities.length >= PRIORITY_CAP;

  const shown = priorities.slice(0, PHONE_ROW_CAP);
  const overflow = priorities.slice(PHONE_ROW_CAP);

  function toggle(priority: PriorityView) {
    setOrphanedId(null);
    if (priority.completed) {
      if (priority.entryEventId) {
        void api.revertLogEntry(priority.entryEventId).then(onChanged);
      }
      return;
    }
    void api
      .completePriority(priority.priorityId, date)
      .then(onChanged)
      .catch(() => setOrphanedId(priority.priorityId));
  }

  function remove(priority: PriorityView) {
    void api.removePriority(priority.priorityId).then(onChanged);
  }

  // ux-designer audit flagged this summary as missing the "Priorities · "
  // prefix from today-phone-parity.md §4.1's copy table. Deliberately NOT
  // added: CollapsibleCard renders `title` and `summary` as two separate
  // always-visible text spans with no aria-label, so their text
  // concatenates into one accessible name -- prefixing the summary would
  // make both the visible text AND the announced name repeat "Priorities"
  // twice back-to-back. Spec updated to match (today-phone-parity.md §4.1
  // changelog) rather than introducing that duplication.
  const summary = priorities.length > 0 ? `${doneCount} of 5 done` : undefined;

  return (
    <CollapsibleCard title="Priorities" summary={summary} isLast={isLast}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minHeight: 'var(--touch-target-min)',
            color: 'var(--hue-primary)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          <Plus size={14} aria-hidden="true" />
          Add
        </button>
      </div>

      <div>
        {priorities.length === 0 && !composerOpen ? (
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Nothing pinned as a priority yet today.
          </p>
        ) : (
          shown.map((priority, i) => (
            <PriorityRowPhone
              key={priority.priorityId}
              priority={priority}
              isLast={i === shown.length - 1 && overflow.length === 0}
              orphaned={priority.priorityId === orphanedId}
              onToggle={() => toggle(priority)}
              onRemove={() => remove(priority)}
              onOpenObjective={onOpenObjective}
            />
          ))
        )}

        {overflow.length > 0 && (
          <button
            type="button"
            onClick={() => setOverflowOpen(true)}
            style={{
              marginTop: 'var(--space-2)',
              fontSize: 'var(--type-caption-size)',
              color: 'var(--text-secondary)',
              minHeight: 'var(--touch-target-min)',
            }}
          >
            +{overflow.length} more
          </button>
        )}

        {composerOpen && (
          <PriorityComposerPhone
            date={date}
            objectives={objectives}
            capReached={capReached}
            onClose={() => setComposerOpen(false)}
            onCreated={() => {
              setComposerOpen(false);
              onChanged();
            }}
          />
        )}
      </div>

      {overflowOpen && (
        <ListSheet title="Priorities" onClose={() => setOverflowOpen(false)}>
          {overflow.map((priority, i) => (
            <PriorityRowPhone
              key={priority.priorityId}
              priority={priority}
              isLast={i === overflow.length - 1}
              orphaned={priority.priorityId === orphanedId}
              onToggle={() => toggle(priority)}
              onRemove={() => remove(priority)}
              onOpenObjective={onOpenObjective}
            />
          ))}
        </ListSheet>
      )}
    </CollapsibleCard>
  );
}

function PriorityRowPhone({
  priority,
  isLast,
  orphaned,
  onToggle,
  onRemove,
  onOpenObjective,
}: {
  readonly priority: PriorityView;
  readonly isLast: boolean;
  readonly orphaned: boolean;
  readonly onToggle: () => void;
  readonly onRemove: () => void;
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <CheckControl
          checked={priority.completed}
          onToggle={onToggle}
          label={priority.completed ? `${priority.title}, completed. Tap to undo.` : `Mark ${priority.title} complete`}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 500,
              color: priority.completed ? 'var(--text-muted)' : 'var(--text-primary)',
            }}
          >
            {priority.title}
          </div>
          <div style={{ marginTop: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {priority.linkedAction ? (
              <ObjectiveChipPhone
                title={priority.linkedAction.objectiveTitle}
                tokenPrefix={objectiveCategoryTokenPrefix(priority.linkedAction.objectiveCategory)}
                onSelect={() => onOpenObjective(priority.linkedAction!.objectiveId)}
              />
            ) : (
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>Standalone</span>
            )}
            {priority.timeLabel && (
              <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
                {priority.timeLabel}
              </span>
            )}
          </div>
        </div>
        {/* Always-visible icon affordance at reduced weight — no hover on a
            touch surface (§9 item 2). Mirrors desktop's own PrioritiesCard
            exactly: that card's row-hover reveal is a single Remove action
            (a prior "expand"/"comment" pair was removed there as dead UI —
            no per-Priority notes field exists anywhere in PriorityView/the
            ADR, per that file's own bug-fix note), so phone has nothing
            further to make always-visible beyond this one real action. */}
        <div style={{ display: 'flex', gap: 'var(--space-1)', flex: 'none' }}>
          <button
            type="button"
            aria-label={`Remove ${priority.title}`}
            onClick={onRemove}
            style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      {orphaned && (
        <p role="status" style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'calc(var(--touch-target-min) + var(--space-3))', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Its linked action was removed, so this can&apos;t be completed anymore — remove it, or leave it as a record.
        </p>
      )}
    </div>
  );
}

function ObjectiveChipPhone({
  title,
  tokenPrefix,
  onSelect,
}: {
  readonly title: string;
  readonly tokenPrefix: string | null;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Open ${title}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: 'var(--type-caption-size)',
        color: 'var(--text-secondary)',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-pill)',
        padding: '2px 10px',
        maxWidth: '100%',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: tokenPrefix ? `var(${tokenPrefix}-vivid)` : 'var(--text-muted)',
          flex: 'none',
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
    </button>
  );
}

function PriorityComposerPhone({
  date,
  objectives,
  capReached,
  onClose,
  onCreated,
}: {
  readonly date: IsoDate;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly capReached: boolean;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const api = useDomainApi();
  const [title, setTitle] = useState('');
  const [linkedAction, setLinkedAction] = useState<{ readonly objectiveId: ObjectiveId; readonly actionId: ActionId } | null>(null);
  const [linkedLabel, setLinkedLabel] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (capReached) {
    return (
      <div style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--border-subtle)' }}>
        <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Five&apos;s the limit for today — finish or clear one first to add another.
        </p>
        <button type="button" onClick={onClose} style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)' }}>
          Close
        </button>
      </div>
    );
  }

  async function submit() {
    if (title.trim().length === 0) return;
    await api.createPriority(date, { title: title.trim(), linkedAction, timeLabel: null });
    onCreated();
  }

  return (
    <div style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <input
        autoFocus
        aria-label="New Priority title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="What's the priority?"
        style={INPUT_STYLE}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', minHeight: 'var(--touch-target-min)' }}
        >
          {linkedLabel ?? 'Standalone'}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {pickerOpen && (
          <ObjectiveActionPickerPopoverPhone
            objectives={objectives}
            onSelect={(objectiveId, actionId, label) => {
              setLinkedAction({ objectiveId, actionId });
              setLinkedLabel(label);
              setPickerOpen(false);
            }}
            onClearLink={() => {
              setLinkedAction(null);
              setLinkedLabel(null);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onClose} style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          style={{
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

/**
 * Objective+action picker for linking a Priority on phone — same
 * two-step interaction shape as desktop's `ObjectiveActionPickerPopover`
 * (PrioritiesCard.tsx), reused verbatim per the phase spec's "the picker
 * interaction itself is not new to phone" ruling (§2's Brain-dump reasoning
 * applies identically here).
 */
function ObjectiveActionPickerPopoverPhone({
  objectives,
  onSelect,
  onClearLink,
  onClose,
}: {
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly onSelect: (objectiveId: ObjectiveId, actionId: ActionId, label: string) => void;
  readonly onClearLink: () => void;
  readonly onClose: () => void;
}) {
  const api = useDomainApi();
  const [chosenObjective, setChosenObjective] = useState<ObjectiveView | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Link to an objective action"
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-2))',
        left: 0,
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        maxHeight: '260px',
        overflowY: 'auto',
      }}
    >
      {!chosenObjective ? (
        <>
          <PickerOptionRow label="Standalone" selected={false} onSelect={onClearLink} />
          {objectives.map((o) => (
            <PickerOptionRow
              key={o.objectiveId}
              label={o.title}
              selected={false}
              onSelect={() => void api.getObjective(o.objectiveId).then(setChosenObjective)}
            />
          ))}
        </>
      ) : chosenObjective.actions.filter((a) => !a.completed).length === 0 ? (
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', padding: '0 var(--space-2)' }}>
          No open actions on this objective.
        </p>
      ) : (
        chosenObjective.actions
          .filter((a) => !a.completed)
          .map((a) => (
            <PickerOptionRow
              key={a.actionId}
              label={a.title}
              selected={false}
              onSelect={() => onSelect(chosenObjective.objectiveId, a.actionId, `${chosenObjective.title} · ${a.title}`)}
            />
          ))
      )}
    </div>
  );
}
